import { uniqBy } from 'es-toolkit';
import React, { useMemo, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { Box, InputAdornment, TablePagination, TextField } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';

import { TooltipIconButton } from '@paragon/client-tools';
import { ModelCategory } from '@paragon/contract-library';
import { getChildrenFinder, PathUtilities } from '@paragon/layout-models';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { createScrollbarStyling } from '@paragon/themes';
import { sortBy } from '@paragon/utilities';

import { copyComponentDesignsToContainer, moveComponentDesignsToContainer } from '../../store/actions/components';
import { ORDER_BY_MAPPINGS } from '../../store/grid/ordering';
import { getMaxPageIndex, PAGE_SIZE } from '../../store/grid/paging';
import { getScopeProjectGuids, ProjectActionScope } from '../../store/projectActionScope';
import { getProjects } from '../../store/selectors/layoutModels';
import { useAppDispatch, useAppSelector } from '../hooks';

export interface MoveCopyDestinationDialogProps {
  readonly intent: 'copy' | 'move';
  readonly scope: ProjectActionScope;
}

export function MoveCopyDestinationDialog({ intent, scope }: MoveCopyDestinationDialogProps) {
  const dispatch = useAppDispatch();
  const projects = useAppSelector(getProjects);
  const getChildren = useAppSelector(getChildrenFinder);

  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [targetPath, setTargetPath] = useState<string | null>(null);

  const matchingProjects = useMemo(() => {
    // copying into the source project isn't allowed
    const excludedProjectGuids = new Set(intent === 'copy' ? getScopeProjectGuids(scope) : []);

    const actualSearch = search.trim().toLowerCase();
    const matches = projects.filter(
      (project) => !excludedProjectGuids.has(project.guid) && project.name.toLowerCase().includes(actualSearch),
    );
    return sortBy(matches, ORDER_BY_MAPPINGS.createdAt, 'desc');
  }, [projects, search, intent, scope]);

  const activePage = Math.min(pageIndex, getMaxPageIndex(matchingProjects.length));
  const visibleProjects = matchingProjects.slice(activePage * PAGE_SIZE, (activePage + 1) * PAGE_SIZE);

  const renderNode = (parentPath: string | null, guid: string, name: string): React.ReactNode => {
    const path = parentPath ? PathUtilities.createFrom(parentPath, guid) : guid;
    const children = uniqBy(
      getChildren(guid).filter(
        (child) => child.category === ModelCategory.Project || child.category === ModelCategory.IntermediateModel,
      ),
      (child) => child.guid,
    );

    return (
      <TreeItem key={path} itemId={path} label={name}>
        {children.map((child) => renderNode(path, child.guid, child.name))}
      </TreeItem>
    );
  };

  const changeSearch = (newSearch: string) => {
    setSearch(newSearch);

    setPageIndex(0);
    setTargetPath(null);
  };

  const confirm = () => {
    if (!targetPath) return;

    dispatch(closeCurrentPopup());

    const targetContainerGuid = PathUtilities.getGuid(targetPath);
    dispatch(
      intent === 'copy'
        ? copyComponentDesignsToContainer(scope, targetContainerGuid)
        : moveComponentDesignsToContainer(scope, targetContainerGuid),
    );
  };

  return (
    <Dialog
      fullWidth
      disableRestoreFocus // so `autoFocus` works (see https://stackoverflow.com/a/76533962/4046605)
      title={intent === 'copy' ? 'Copy components to…' : 'Move components to…'}
      submitText={intent === 'copy' ? 'Copy here' : 'Move here'}
      onSubmit={confirm}
      submitDisabled={!targetPath}
      onCancel="close"
      maxWidth="sm"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField
          size="small"
          fullWidth
          autoFocus
          autoComplete="off"
          placeholder="Search projects…"
          value={search}
          onChange={(event) => changeSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.code === 'Escape' && search !== '') {
              event.stopPropagation();
              changeSearch('');
            }
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: search !== '' && (
                <InputAdornment position="end">
                  <TooltipIconButton enableInteractive title="Clear Search" onClick={() => changeSearch('')}>
                    <ClearIcon />
                  </TooltipIconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        <Box sx={[{ height: 360, overflow: 'auto' }, createScrollbarStyling]}>
          <SimpleTreeView selectedItems={targetPath} onSelectedItemsChange={(_event, itemId) => setTargetPath(itemId)}>
            {visibleProjects.map((project) => renderNode(null, project.guid, project.name))}
          </SimpleTreeView>
        </Box>

        <TablePagination
          rowsPerPageOptions={[PAGE_SIZE]}
          component="div"
          count={matchingProjects.length}
          rowsPerPage={PAGE_SIZE}
          page={activePage}
          onPageChange={(_event, newPageIndex) => setPageIndex(newPageIndex)}
        />
      </Box>
    </Dialog>
  );
}
